import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { grey } from "@mui/material/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MemoryIcon from "@mui/icons-material/Memory";
import SettingsIcon from "@mui/icons-material/Settings";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpBomService from "@plannel/services/fp/fp-bom-service";
import fpItemService from "@plannel/services/fp/fp-item-service";
import FlowChart from "@plannel/pages/factory-plan/common/component/FlowChart";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import { bomDataToFlow } from "@plannel/pages/factory-plan/common/flowchart";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

const FORWARD = "forward";
const BACKWARD = "backward";

// old bom/Bom.jsx의 setRadioButton — 품목 구분에 따라 선택 가능한 전개 방향이 달라진다.
// 원자재(M)는 역전개만(그 품목을 쓰는 상위 BOM), 완제품(P)은 정전개만(그 품목을 만드는 BOM),
// 반제품(I) 등 나머지는 둘 다 가능하고 기본은 정전개.
const directionRule = (itemClassCd) => {
  if (itemClassCd === "M") return { forwardEnabled: false, backwardEnabled: true, initial: BACKWARD };
  if (itemClassCd === "P") return { forwardEnabled: true, backwardEnabled: false, initial: FORWARD };
  return { forwardEnabled: true, backwardEnabled: true, initial: FORWARD };
};

// 트리 노드의 품목 구분별 아이콘 — old setTreeOptions의 iconImages
// ["cube_yellow_24.png"(0), "gear_24.png"(1), "cpu.png"(2)] + 백엔드 bomRoutingDtoToMap의
// icon 채번(M=2, I=1, 그 외=0)을 그대로 옮긴 것. MUI 아이콘 선택은 report/OrderTracking.js의
// 같은 3종 매핑(cube→Inventory2, gear→Settings, cpu→Memory)을 재사용한다.
const TreeItemIcon = ({ icon }) => {
  if (icon === 2) return <MemoryIcon fontSize="small" sx={{ color: "text.secondary" }} />;
  if (icon === 1) return <SettingsIcon fontSize="small" sx={{ color: "text.secondary" }} />;
  return <Inventory2Icon fontSize="small" sx={{ color: "warning.light" }} />;
};

// old dataProvider.setObjectRows({children: res.data}, 'children', '', 'icon') — 백엔드가 각 노드에
// 자기 자식을 `children`으로 담은 재귀 트리를 준다. AG Grid Tree Data는 경로 배열(getDataPath)을
// 요구하므로 재귀 순회하며 조상 체인을 경로로 만들어 평탄화한다(report/OrderTracking.js와 동일 패턴).
// 경로 세그먼트는 `itemCd__형제인덱스`로 만들어 같은 품목이 형제로 두 번 나와도 경로가 고유하다
// (표시값은 level/itemCd 필드를 직접 읽으므로 이 접미사는 화면에 노출되지 않는다).
const flattenBomTree = (nodes, parentPath = []) => {
  const result = [];
  (nodes ?? []).forEach((node, index) => {
    const path = [...parentPath, `${node.itemCd ?? index}__${index}`];
    result.push({
      level: node.level,
      icon: node.icon,
      itemCd: node.itemCd,
      itemNm: node.itemNm,
      itemClassCd: node.itemClassCd,
      itemUom: node.itemUom,
      bomRate: node.bomRate,
      productRate: node.productRate,
      path
    });
    if (node.children?.length) result.push(...flattenBomTree(node.children, path));
  });
  return result;
};

/**
 * BOM — 품목 1건을 골라 그 품목의 BOM 전개를 다이어그램/트리로 보여주는 조회 전용 화면.
 * old `view/factoryplan/master/bom/Bom.jsx` + `BomDiagram.jsx` 이관.
 *
 * 표준 Master CRUD 그리드(Route.js)와 달리 저장/삭제가 없고, 좌측 품목 목록(마스터) →
 * 우측 BOM 전개(디테일, 다이어그램 탭 + 트리 그리드 탭) 구성이다. 다이어그램은 FLO 파일럿에서
 * 만든 공용 엔진(`common/flowchart.js` + `common/component/FlowChart.js`/`FlowNode.js`)을
 * 그대로 재사용한다(fp-master-screen-guide.md §14·§15).
 */
const Bom = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  // old InputField name="itemParam"(label FP_ITEM) — 전량 로드된 품목 목록을 클라이언트에서
  // 즉시 필터한다(§6-1 케이스3, Bom은 응답에 itemCd/itemNm이 둘 다 있어 예외 대상이 아니다).
  const [itemSearch, setItemSearch] = useState(reduxViewState?.itemCdNm ?? "");

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [direction, setDirection] = useState(FORWARD);

  const [bomTree, setBomTree] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const [tabIndex, setTabIndex] = useState(0);
  const [progressOn, setProgressOn] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const itemGridRef = useRef();
  const treeGridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  // 조회 토큰 가드(§0-8) — 조회가 3단(품목 목록 / BOM 트리 / BOM 다이어그램)이라 가드도 3개.
  const latestItemReqRef = useRef(0);
  const latestTreeReqRef = useRef(0);
  const latestDiagramReqRef = useRef(0);

  const itemGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName: `${viewName}_ITEM` });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // old BaseGrid(setNoneEditableGrid) — 읽기 전용 단일 선택 목록. 행 클릭으로 우측을 조회한다.
      rowSelection: { ...base.rowSelection, mode: "singleRow" }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const treeGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName: `${viewName}_TREE` });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      treeData: true,
      getDataPath: (data) => data.path,
      // old gridView.expandAll() — 로드 후 전체 전개.
      groupDefaultExpanded: -1
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old lookupDisplay+lookupData(코드 대신 다국어 라벨 표시) 대응 — §8-2 valueFormatter 패턴.
  const itemClassFormatter = (params) => {
    const key = `FP_ITEM_CLASS_CD_${params.value}`;
    const label = t(key);
    return label !== key ? label : params.value;
  };

  // old bomItemGridItems 5개(id 숨김 포함)와 1:1.
  // TODO(MIG-T2): [bom/Bom] 품목 그리드 컬럼 필터(old autoFilter) 미이관.
  //   old: itemCd/itemNm/itemClassCd 컬럼에 autoFilter: true(컬럼 헤더 드롭다운 값 필터).
  //   사유: 컬럼 필터는 Tier2(saas-fp/docs/fp-frontend-migration/CLAUDE.md) — 조회조건 품목 검색(클라 필터)으로 1차 대체.
  const itemColumnDefs = useMemo(() => [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 180, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", width: 240, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", width: 120,
      type: ["nonEditableColumn", "centerAligned"], valueFormatter: itemClassFormatter },
    { headerName: "FP_ITEM_UOM", field: "itemUom", colId: "itemUom", width: 120, type: ["nonEditableColumn", "centerAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old bomTreeGridItems 8개 중 level은 트리 표시 컬럼(autoGroupColumnDef)으로 옮겨간다 —
  // 나머지 7개(icon 숨김 포함)가 여기 있고, 합계는 old와 동일하게 8개다.
  const treeColumnDefs = useMemo(() => [
    { headerName: "FP_ICON_NM", field: "icon", colId: "icon", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 180, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", width: 240, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", width: 110,
      type: ["nonEditableColumn", "centerAligned"], valueFormatter: itemClassFormatter },
    { headerName: "FP_ITEM_UOM", field: "itemUom", colId: "itemUom", width: 120, type: ["nonEditableColumn", "centerAligned"] },
    { headerName: "FP_BOM_RATE", field: "bomRate", colId: "bomRate", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "FP_PRODUCT_RATE", field: "productRate", colId: "productRate", width: 130, type: ["nonEditableColumn", "numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old bomTreeGridItems의 첫 표시 컬럼(level) — RealGrid TreeGrid는 첫 컬럼에 트리 계층과
  // 아이콘을 함께 그린다. AG Grid는 그 자리가 autoGroupColumnDef다.
  const autoGroupColumnDef = useMemo(() => ({
    headerName: t("FP_LEVEL"),
    // 트리 들여쓰기는 깊이당 28px씩 늘어난다. old RealGrid 폭(240px)을 그대로 쓰면 **깊이 9부터
    // 아이콘·레벨·건수가 컬럼 밖으로 밀려 잘려서**, 데이터는 멀쩡한데 하위 경로가 통째로
    // 안 보이는 것처럼 보인다(2026-08-14 실측: 샘플 BOM이 깊이 15까지 간다).
    // 400px = 깊이 13까지. 그보다 깊으면 잘리는 건 감수한다(2026-08-14 사용자 결정 — flex로
    // 남는 공간을 다 먹게 했더니 너무 넓었다). 더 봐야 하면 컬럼 경계를 드래그해 넓히면 된다.
    width: 400,
    type: ["nonEditableColumn"],
    valueGetter: (params) => params.data?.level,
    cellRendererParams: {
      innerRenderer: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TreeItemIcon icon={params.data?.icon} />
          <span>{params.value}</span>
        </Box>
      )
    }
  }), [t]);

  // 품목 코드/명칭 부분일치(대소문자 무시) — 서버 ItemQueryRepository의 itemCdNm 조건과 동일 규칙.
  const doesExternalFilterPass = (node) => {
    if (!itemSearch) return true;
    const needle = itemSearch.toLowerCase();
    const hit = (hay) => (hay ?? "").toLowerCase().includes(needle);
    return hit(node.data?.itemCd) || hit(node.data?.itemNm);
  };

  const isExternalFilterPresent = () => !!itemSearch;

  useEffect(() => {
    itemGridRef.current?.api?.onFilterChanged();
  }, [itemSearch]);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, itemCdNm: itemSearch });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, itemSearch]);

  const clearDetails = () => {
    setSelectedItem(null);
    setBomTree([]);
    setNodes([]);
    setEdges([]);
  };

  const retrieveItems = () => {
    if (!planScope) return;

    const reqId = ++latestItemReqRef.current;
    itemGridRef.current?.api?.setGridOption("loading", true);

    fpItemService.getItems({ planScope })
      .then((response) => {
        if (reqId !== latestItemReqRef.current) return;
        const results = response.data;

        if (isEmpty(results)) {
          setItems([]);
          clearDetails();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        setItems(results);
        // old loadItem — 첫 행을 선택하고 그 품목의 BOM을 바로 조회한다.
        selectItem(results[0]);
      })
      .catch((e) => debug(e))
      .then(() => {
        if (reqId !== latestItemReqRef.current) return;
        itemGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveBomTree = (item, bomDirection) => {
    if (!planScope || !item?.itemCd) return;

    const reqId = ++latestTreeReqRef.current;
    treeGridRef.current?.api?.setGridOption("loading", true);

    // 정전개는 bomTpCd 'P'(이 품목을 만드는 BOM), 역전개는 'C'(이 품목을 쓰는 BOM).
    const backward = bomDirection === BACKWARD;
    const params = { viewId: viewName, subId: "", planScope, itemCd: item.itemCd, bomTpCd: backward ? "C" : "P" };
    const request = backward ? fpBomService.getBomTreeReverse(params) : fpBomService.getBomTree(params);

    request
      .then((response) => {
        if (reqId !== latestTreeReqRef.current) return;
        setBomTree(flattenBomTree(response.data));
      })
      .catch((e) => {
        debug(e);
        if (reqId === latestTreeReqRef.current) setBomTree([]);
      })
      .then(() => {
        if (reqId === latestTreeReqRef.current) treeGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveBomDiagram = (item, bomDirection) => {
    if (!planScope || !item?.itemCd) return;

    const reqId = ++latestDiagramReqRef.current;
    // 다이어그램은 그리드가 아니라 ReactFlow라 그리드 로딩 오버레이를 쓸 수 없다(§14-4).
    setProgressOn(true);

    const backward = bomDirection === BACKWARD;
    const params = { viewId: viewName, subId: "", planScope, itemCd: item.itemCd, bomTpCd: backward ? "C" : "P" };
    const request = backward ? fpBomService.getBomDiagramReverse(params) : fpBomService.getBomDiagram(params);

    request
      .then((response) => {
        if (reqId !== latestDiagramReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setNodes([]);
          setEdges([]);
          return;
        }
        // 응답 배열 = 선택 품목에 걸린 공정별 트리(대체 공정). 예전엔 [0]만 그려서 나머지를 버렸다.
        const flow = bomDataToFlow(results, t, bomDirection);
        setNodes(flow.nodes);
        setEdges(flow.edges);
      })
      .catch((e) => {
        debug(e);
        if (reqId !== latestDiagramReqRef.current) return;
        setNodes([]);
        setEdges([]);
      })
      .then(() => {
        if (reqId === latestDiagramReqRef.current) setProgressOn(false);
      });
  };

  // old setDetails — 품목을 고르면 그 품목 구분에 맞는 전개 방향으로 맞춘 뒤 BOM을 조회한다.
  const selectItem = (item) => {
    if (!item) return;
    const nextDirection = directionRule(item.itemClassCd).initial;
    setSelectedItem(item);
    setDirection(nextDirection);
    retrieveBomTree(item, nextDirection);
    retrieveBomDiagram(item, nextDirection);
  };

  const onDirectionChange = (event) => {
    const nextDirection = event.target.value;
    if (nextDirection === direction) return;
    setDirection(nextDirection);
    retrieveBomTree(selectedItem, nextDirection);
    retrieveBomDiagram(selectedItem, nextDirection);
  };

  const onItemGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: itemColumnDefs, viewName: `${viewName}_ITEM`, initState: true });
  };

  const onTreeGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: treeColumnDefs, viewName: `${viewName}_TREE`, initState: true });
  };

  // old onCellClicked은 같은 행을 다시 눌러도 setDetails를 다시 부른다(= 전개 방향이 그 품목의
  // 기본값으로 되돌아가고 재조회된다) — 그 동작을 그대로 유지한다.
  const onItemRowClicked = (e) => {
    if (!e.data) return;
    selectItem(e.data);
  };

  // old는 품목 검색이 서버 재조회였고 그 직후 첫 행을 선택해 우측 BOM까지 같이 바꿨다
  // (loadItem의 setCurrent(0)+setDetails). new는 클라이언트 필터라 목록만 줄어들 뿐이므로,
  // 선택 품목이 필터에서 빠지면 첫 표시행으로 옮겨 우측도 같이 갱신한다(안 하면 화면에 보이지
  // 않는 품목의 BOM이 남는다).
  const onItemFilterChanged = (e) => {
    let firstNode = null;
    let selectedVisible = false;

    e.api.forEachNodeAfterFilter((node) => {
      if (!firstNode) firstNode = node;
      if (node.data?.itemCd === selectedItem?.itemCd) selectedVisible = true;
    });

    if (selectedVisible) return;
    if (!firstNode) {
      clearDetails();
      return;
    }

    firstNode.setSelected(true, true);
    selectItem(firstNode.data);
  };

  // old loadItem의 gridView.setCurrent({itemIndex: 0}) — 조회 직후 첫 행을 실제로 선택 표시한다.
  const onItemRowDataUpdated = (params) => {
    if (params.api.getSelectedNodes().length > 0) return;
    params.api.getDisplayedRowAtIndex(0)?.setSelected(true);
  };

  // 서버 조회는 planScope(스코프 파티션) 변경 시에만 — 품목 검색어는 클라이언트 필터다(§6-1).
  useEffect(() => {
    if (!gridLoading) return;
    retrieveItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  // 노드 더블클릭 → 해당 기준정보 화면으로 딥링크 이동(§13-1). old는 history.push({pathname,state}).
  const onNodeDoubleClick = (event, node) => {
    const nav = node?.data?.nav;
    if (!nav?.reduxKey) return;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(nav.reduxKey, nav.fields)));
    reduxDispatch(reduxUtil.updateHistoryState(nav.reduxKey));
  };

  // old setBomTitleText — "품목 : 코드 (명칭, 품목구분, 생산단위)".
  const bomItemTitle = useMemo(() => {
    if (!selectedItem) return "";
    const classLabel = itemClassFormatter({ value: selectedItem.itemClassCd });
    return `${t("FP_ITEM")} : ${selectedItem.itemCd} (${selectedItem.itemNm}, ${classLabel}, ${selectedItem.itemUom})`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, t]);

  const { forwardEnabled, backwardEnabled } = directionRule(selectedItem?.itemClassCd);

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <TextField
          label={t("FP_ITEM")}
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          size="small"
          sx={{ ml: 1.5, minWidth: 260 }}
        />
        {/* 버튼 그룹이 없는 화면이라 빈 Box를 마지막 자식으로 둔다(§6 — 안 그러면 품목 검색칸이
            float:right로 밀려 PlanScope와 멀어진다). */}
        <Box />
      </FilterContainer>
      {/* TODO(MIG-T2): [bom/Bom] 좌우 분할 드래그 리사이즈 미이관.
            old: <ResultArea sizes={[33, 67]} direction="horizontal">로 경계를 끌어 비율 조절 가능.
            사유: 장식성 레이아웃(Tier2) — 비율만 flex로 고정한다(fp-master-screen-guide.md §15-1). */}
      {/* 아래 여백(mb)은 좌/우 각 그리드가 아니라 **이 공통 컨테이너에만** 준다 — 한쪽 그리드에만
          주면 그 그리드가 8px 짧아져 좌우 하단이 어긋나 보인다(우측 탭 그리드에서 실제로 발생). */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, mt: 1, mb: 1 }}>
        {/* 좌: 품목 목록(마스터). old ResultArea sizes={[33, 67]} 비율 유지. */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 0.33, minHeight: 0, mr: 1 }}>
          {/* 좌/우 섹션 제목은 같은 스타일로 맞춘다(우측은 뒤에 품목 정보가 붙는다). 굵기는 코드베이스
              표준인 500 — 그리드 위 섹션 라벨의 지배적 형태가 `Typography variant="subtitle1"` +
              `fontWeight: 500`이다(RpDistributionNetwork.js:748·767·799·815,
              InventoryManagementOverview.js:473·492·504 등). 여기서 Typography를 쓰지 않는 건
              subtitle1의 16px이 그리드 헤더보다 커져서다 — 크기는 본문 그대로 두고 굵기만 맞춘다. */}
          <Box sx={{ mb: 1, minHeight: 40, display: "flex", alignItems: "center", fontWeight: 500 }}>{t("FP_ITEM")}</Box>
          <Box id="fp-bom-item-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
            <AgGridReact
              ref={itemGridRef}
              rowData={items}
              {...itemGridMemo}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              onRowClicked={onItemRowClicked}
              onRowDataUpdated={onItemRowDataUpdated}
              onFilterChanged={onItemFilterChanged}
              onGridReady={onItemGridReady}
            />
          </Box>
        </Box>
        {/* 우: 선택 품목의 BOM 전개(디테일) — 제목/전개방향 + 다이어그램·트리 탭. */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 0.67, minHeight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 40 }}>
            {/* old는 섹션 제목(ButtonArea title=FP_BOM)과 선택 품목 정보(회색 바 안의 굵은 텍스트)가
                서로 다른 영역이었다 — 한 줄로 합치면서 두 텍스트가 이어져 붙어 보이던 것을
                구분선 + 강조/보조 색상 대비로 분리한다. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{t("FP_BOM")}</Box>
              {bomItemTitle && (
                <>
                  {/* 색: 기본 divider(rgba(0,0,0,0.12) ≒ #e0e0e0)는 흰 배경 + 회색 보조 텍스트 사이에서
                      거의 안 보인다(테마에 palette.divider 오버라이드가 없다) → 한 단계만 어두운
                      grey[400](#bdbdbd), 두께는 1px 유지.
                      높이: `flexItem`(= align-self:stretch + height:auto)은 이 라벨 Box의 flex line
                      높이(= 텍스트 content 21px)까지만 늘어나서 my:0.75(위아래 6px)를 빼면 선이 9px밖에
                      안 됐다 — 행의 minHeight:40은 바깥 Box 것이라 여기까지 오지 않는다. 그래서
                      flexItem 대신 **명시 높이 16px + alignSelf:center**로 바꿨다(14px 폰트의 line box
                      21px보다 작고 대문자 높이보다는 커서 구분선으로 읽히는 비율. 20px 이상은 행을
                      꽉 채워 표 구분선처럼 무거워진다). 정렬도 마진 대칭에 의존하지 않고 명시한다. */}
                  <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", borderColor: grey[400] }} />
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {bomItemTitle}
                  </Box>
                </>
              )}
            </Box>
            <RadioGroup row value={direction} onChange={onDirectionChange}>
              <FormControlLabel
                value={FORWARD}
                label={t("FP_BOM_FORWARD")}
                control={<Radio size="small" disabled={!selectedItem || !forwardEnabled} />}
              />
              <FormControlLabel
                value={BACKWARD}
                label={t("FP_BOM_BACKWARD")}
                control={<Radio size="small" disabled={!selectedItem || !backwardEnabled} />}
              />
            </RadioGroup>
          </Box>
          {/* 탭은 MUI Tabs 직접 사용 + 항상 마운트 후 display 토글(§11-1). */}
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 38 }}>
            <Tab label={t("BOM_DIAGRAM")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
            <Tab label={t("BOM_TREE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
          </Tabs>
          <Box sx={{ display: tabIndex === 0 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {/* 트리 탭과 동일하게 flex:1 + minHeight:0 — 높이가 없으면 ReactFlow가 0px로 렌더된다(§14-4). */}
            <Box id="fp-bom-diagram" className="grid-area" sx={{ flex: 1, minHeight: 0 }}>
              <FlowChart nodes={nodes} edges={edges} onNodeDoubleClick={onNodeDoubleClick} />
            </Box>
          </Box>
          <Box sx={{ display: tabIndex === 1 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box id="fp-bom-tree-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
              <AgGridReact
                ref={treeGridRef}
                rowData={bomTree}
                autoGroupColumnDef={autoGroupColumnDef}
                {...treeGridMemo}
                onGridReady={onTreeGridReady}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Bom);
